import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, CheckCircle, Upload } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../../../../../context/auth/AuthContext';
import CustomInput from '../../../../../../../components/forms/CustomInput';

const SignUpForm = ({ onSwitchToLogin, referredAgent }) => {
  const navigate = useNavigate();
  const { sendSignupSmsOtp, verifySignupPhoneOtp, uploadLogo, registerAgent } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Phone verification state
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isOtpSent, setIsOtpSent] = useState(false);

  // Registration form state
  const [formData, setFormData] = useState({
    companyName: '',
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    pincode: '',
    city: ''
  });
  const [logoFile, setLogoFile] = useState(null);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!phoneNumber || phoneNumber.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendSignupSmsOtp({
        phone_number: phoneNumber
      });

      if (response.message === 'OTP sent successfully') {
        setIsOtpSent(true);
        toast.success('OTP sent successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const response = await verifySignupPhoneOtp({
        phone_number: phoneNumber,
        otp: otpString
      });

      if (response.message === 'OTP verified successfully' || response.success) {
        setIsPhoneVerified(true);
        setFormData((prev) => ({ ...prev, phone: phoneNumber }));
        toast.success('Phone verified!');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value !== '' && index < 5) {
        document.querySelector(`input[name='signup-otp-${index + 1}']`)?.focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pastedData[i] || '';
    }
    setOtp(newOtp);
    const nextEmptyIndex = newOtp.findIndex((val) => val === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    document.querySelector(`input[name='signup-otp-${focusIndex}']`)?.focus();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setLogoFile(e.target.files[0]);
    e.target.value = '';
  };

  const validateForm = () => {
    const mandatoryFields = {
      fullName: 'Full Name',
      email: 'Email',
      companyName: 'Company Name'
    };

    for (const key in mandatoryFields) {
      if (!formData[key]?.trim()) {
        toast.error(`${mandatoryFields[key]} is required`);
        return false;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Valid email required');
      return false;
    }

    if (formData.password || formData.confirmPassword) {
      if (!formData.password) {
        toast.error('Please enter a password');
        return false;
      }
      if (formData.password.length < 6) {
        toast.error('Password too short (min 6)');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSignUp = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    let logoUrl = '';
    if (logoFile) {
      try {
        const response = await uploadLogo(logoFile);
        logoUrl = response.fileUrl;
      } catch (error) {
        toast.error('File upload failed');
        setIsLoading(false);
        return;
      }
    }

    try {
      const response = await registerAgent({
        name: formData.fullName,
        email: formData.email,
        phone_number: formData.phone,
        password: formData.password,
        company_name: formData.companyName,
        logo: logoUrl,
        address: formData.address,
        pincode: formData.pincode,
        city: formData.city
      });

      if (response.message === 'The email or phone number is already registered') {
        toast.error(response.message);
        return;
      }

      const registeredIdentifier = formData.phone || formData.email;
      if (registeredIdentifier) {
        localStorage.setItem('pending_agent_identifier', registeredIdentifier);
      }

      toast.success('Registration successful! We are verifying your account.', {
        duration: 3000
      });

      setTimeout(
        () =>
          navigate('/waiting', {
            state: { isWaiting: true, identifier: registeredIdentifier }
          }),
        2000
      );
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      {/* 1. Phone Verification Flow */}
      {!isPhoneVerified && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-3 sm:space-y-3.5 flex-1 flex flex-col"
        >
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Get Started</h2>
            {referredAgent && (
              <p className="text-xs font-semibold text-[#067978] bg-teal-50 px-3 py-1 rounded-xl border border-teal-100 mt-1.5 inline-block">
                Referred by <span className="font-bold">{decodeURIComponent(referredAgent)}</span>
              </p>
            )}
          </div>

          {!isOtpSent ? (
            <form onSubmit={handleSendOtp} className="space-y-3 sm:space-y-3.5">
              <div className="space-y-1">
                <label className="text-xs sm:text-sm font-semibold text-gray-800 ml-1">
                  Phone Number
                </label>
                <div className="bg-white/60 border border-gray-200 rounded-xl px-3.5 py-2 sm:py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center">
                  <span className="text-[#067978] font-bold mr-2.5 text-sm sm:text-base">+91</span>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full bg-transparent text-gray-900 font-medium text-sm sm:text-base outline-none placeholder:text-gray-400"
                    placeholder="9876543210"
                    maxLength="10"
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
              <p className="text-center text-xs sm:text-sm text-gray-600 font-medium pt-2 sm:pt-3">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={onSwitchToLogin}
                  className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
                >
                  Login
                </button>
              </p>
            </form>
          ) : (
            <div className="space-y-3 sm:space-y-3.5">
              <div className="flex justify-between items-center bg-green-50 p-2.5 rounded-xl border border-green-100">
                <div className="text-xs sm:text-sm text-green-800">
                  Code sent to <span className="font-bold">+91 {phoneNumber}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOtpSent(false)}
                  className="text-xs text-[#067978] font-bold hover:underline cursor-pointer"
                >
                  Change
                </button>
              </div>

              <div className="space-y-1">
                <label className="text-xs sm:text-sm font-semibold text-gray-800 ml-1">
                  Enter OTP
                </label>
                <div className="flex gap-2 justify-center">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="tel"
                      name={`signup-otp-${index}`}
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onPaste={handleOtpPaste}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleVerifyOtp();
                        } else if (e.key === 'Backspace' && !digit && index > 0) {
                          document
                            .querySelector(`input[name='signup-otp-${index - 1}']`)
                            ?.focus();
                        }
                      }}
                      className="w-9 h-9 sm:w-11 sm:h-11 bg-white/80 border-2 border-gray-200 rounded-xl text-center text-lg sm:text-xl font-bold text-[#067978] focus:border-[#067978] outline-none transition-all shadow-sm"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={isLoading}
                className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
              >
                {isLoading ? 'Verifying...' : 'Verify Phone Number'}
              </button>

              <p className="text-center text-xs sm:text-sm text-gray-500 font-medium pt-1">
                Didn't receive code?{' '}
                <button
                  type="button"
                  onClick={handleSendOtp}
                  className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
                >
                  Resend
                </button>
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* 2. Registration Form Flow */}
      {isPhoneVerified && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-3 flex-1 flex flex-col"
        >
          <div className="flex items-center justify-between pb-1">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Agent Details</h2>
            </div>
            <div className="px-2.5 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>{' '}
              Verified
            </div>
          </div>

          <form onSubmit={handleSignUp} className="space-y-2.5 sm:space-y-3">
            <div className="space-y-1">
              <label className="text-gray-700 text-[11px] font-bold uppercase ml-1 flex items-center gap-1">
                <Upload size={12} /> Company Logo (Optional)
              </label>
              <label className="bg-white/40 border-2 border-dashed border-gray-300 rounded-xl px-3 py-2 flex items-center justify-center cursor-pointer hover:bg-white/60 transition-all">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {logoFile ? (
                  <div className="flex items-center gap-2 text-[#067978]">
                    <CheckCircle size={15} />
                    <span className="text-xs font-medium truncate max-w-[220px]">
                      {logoFile.name}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400 text-xs flex items-center gap-1.5">
                    <Upload size={13} /> Click to upload logo
                  </span>
                )}
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <CustomInput
                label="Full Name *"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Full Name"
                required
              />
              <CustomInput
                label="Company *"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Company Name"
                required
              />
            </div>

            <CustomInput
              label="Email Address *"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="agent@example.com"
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-800 ml-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password (optional)"
                    className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#067978] pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#067978] transition-colors cursor-pointer"
                  >
                    {showPassword ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-800 ml-1">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    className="w-full bg-white/60 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#067978] pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#067978] transition-colors cursor-pointer"
                  >
                    {showConfirmPassword ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                </div>
              </div>
            </div>

            <CustomInput
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Office / Business address"
            />

            <div className="grid grid-cols-2 gap-3">
              <CustomInput
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
              />
              <CustomInput
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="Pincode"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-1 py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-lg transition-all cursor-pointer"
            >
              {isLoading ? 'Registering...' : 'Create Account'}
            </button>
          </form>
        </motion.div>
      )}
    </div>
  );
};

export default SignUpForm;
